import { Component, OnInit, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Apollo, gql } from 'apollo-angular';
import { ToastrService } from 'ngx-toastr';
import { cloneDeep } from 'lodash';
import { MomentModule } from 'ngx-moment';

import { HeaderComponent } from '../header/header.component';
import { NavComponent } from '../navigation/nav.component';
import { AuthStore } from '@store/auth.store';
import { FleetFeeDistribution, ServiceChargeConfig } from '@interfaces/models.interface';
import { graphQLErrorsOf, isUnsupportedByApi } from '../../../shared/utils/graphql-support.util';
import {
  isMissingPermissionError,
  MISSING_PERMISSION_MESSAGE,
} from '@shared/utils/admin-permission-error.util';
import { AdminPermission } from '@enums/admin-permission.enum';
import { AdminPermissionService } from '@services/admin-permission.service';
import { ADMIN_FLEET_MANAGEMENT_LIST } from '@gql_queries/taxi_fleet/queries';

type FleetType = FleetFeeDistribution['serviceType'];

const FLEET_FEE_DISTRIBUTION_FIELDS = `
  fleetFeeDistributionId
  serviceType
  fleetId
  fleetName
  operatorShareAmount
  driverIncentiveAmount
  isActive
  effectiveFrom
  updatedBy
  dateCreated
  dateUpdated
`;

const FLEET_FEE_DISTRIBUTIONS = gql`
  query fleetFeeDistributions {
    fleetFeeDistributions { ${FLEET_FEE_DISTRIBUTION_FIELDS} }
  }
`;

const UPSERT_FLEET_FEE_DISTRIBUTION = gql`
  mutation upsertFleetFeeDistribution($input: UpsertFleetFeeDistributionInput!) {
    upsertFleetFeeDistribution(input: $input) { ${FLEET_FEE_DISTRIBUTION_FIELDS} }
  }
`;

const UPDATE_FLEET_FEE_DISTRIBUTION_STATUS = gql`
  mutation updateFleetFeeDistributionStatus($input: UpdateFleetFeeDistributionStatusInput!) {
    updateFleetFeeDistributionStatus(input: $input) { ${FLEET_FEE_DISTRIBUTION_FIELDS} }
  }
`;

const SERVICE_CHARGE_CONFIGS = gql`
  query serviceChargeConfigs {
    serviceChargeConfigs { serviceType feeType flatFee }
  }
`;

/** Fleet types in display order. */
const FLEET_TYPE_LABELS: Record<FleetType, string> = {
  taxi: 'Taxi',
  tnvs: 'TNVS',
  truck: 'Truck',
};

/** The booking service whose app fee each fleet type's split is taken from. */
const FLEET_TYPE_SERVICE: Record<FleetType, string> = {
  taxi: 'oktaxi',
  tnvs: 'okride',
  truck: 'express',
};

/**
 * Fleet Fee Distribution (CMS): how each booking's app fee is split between
 * the Fleet Operator, the driver and OKITO, per fleet type.
 *
 * A row without a fleet is the platform default for its fleet type; a row with
 * one overrides it for that Fleet Operator. The backend keeps one row per
 * fleet type and fleet (`upsertFleetFeeDistribution`), so Add refuses a pair
 * that already exists and Edit never changes which pair a row is for. Rows are
 * disabled rather than deleted: a disabled override falls back to the
 * default, and with no enabled default OKITO keeps the whole fee.
 */
@Component({
  selector: 'app-fleet-fee-distribution-cms',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HeaderComponent, NavComponent, MomentModule],
  templateUrl: './fleet-fee-distribution-cms.component.html',
  styleUrl: './fleet-fee-distribution-cms.component.css',
})
export class FleetFeeDistributionCmsComponent implements OnInit {
  constructor(
    private apollo: Apollo,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private router: Router,
    private authStore: AuthStore,
    private permissions: AdminPermissionService,
  ) {}

  /** Changes are offered only with `service_fees.manage`; the API refuses them otherwise. */
  get canManage(): boolean {
    return this.permissions.can(AdminPermission.ServiceFeesManage);
  }

  public readonly fleetTypes = Object.keys(FLEET_TYPE_LABELS) as FleetType[];

  public distributions: FleetFeeDistribution[] = [];
  public filterType: FleetType | '' = '';
  public isLoading = false;
  public loadError: 'none' | 'unsupported' | 'failed' = 'none';
  public isSaving = false;
  public successMessage = '';

  /** App fee per booking service, used to show and check the shares. */
  private serviceCharges: Record<string, Pick<ServiceChargeConfig, 'feeType' | 'flatFee'>> = {};

  /** The row being edited; null while adding. */
  public editing: FleetFeeDistribution | null = null;
  /** The row a status change is being confirmed for. */
  public statusTarget: FleetFeeDistribution | null = null;

  public form = this.emptyForm();
  public fleetOptions: { fleetId: string; operatorName: string; fleetCode: string }[] = [];
  public isLoadingFleets = false;

  private emptyForm() {
    return {
      serviceType: 'taxi' as FleetType,
      scope: 'default' as 'default' | 'fleet',
      fleetId: '' as string,
      operatorShareAmount: null as number | null,
      driverIncentiveAmount: null as number | null,
    };
  }

  ngOnInit(): void {
    this.fetchDistributions();
    this.fetchServiceCharges();
  }

  fleetTypeLabel(serviceType: string): string {
    return FLEET_TYPE_LABELS[serviceType as FleetType] ?? serviceType;
  }

  get visibleDistributions(): FleetFeeDistribution[] {
    return this.filterType
      ? this.distributions.filter((row) => row.serviceType === this.filterType)
      : this.distributions;
  }

  /** The flat app fee for a fleet type, or null when its service is charged a percentage. */
  flatFeeFor(serviceType: FleetType): number | null {
    const config = this.serviceCharges[FLEET_TYPE_SERVICE[serviceType]];
    return config?.feeType === 'flat' ? (config.flatFee ?? 0) : null;
  }

  /** What OKITO keeps of a flat fee; null when the fee is a percentage. */
  okitoShare(row: Pick<FleetFeeDistribution, 'serviceType' | 'operatorShareAmount' | 'driverIncentiveAmount'>): number | null {
    const flatFee = this.flatFeeFor(row.serviceType);
    if (flatFee === null) return null;
    return Math.max(flatFee - row.operatorShareAmount - row.driverIncentiveAmount, 0);
  }

  fetchDistributions(): void {
    this.isLoading = true;
    this.loadError = 'none';

    this.apollo
      .watchQuery({ fetchPolicy: 'network-only', query: FLEET_FEE_DISTRIBUTIONS })
      .valueChanges.subscribe({
        next: (res: any) => {
          this.isLoading = false;
          const rows: FleetFeeDistribution[] = res?.data?.['fleetFeeDistributions'] ?? [];
          this.distributions = cloneDeep(rows).sort(
            (a, b) =>
              this.fleetTypes.indexOf(a.serviceType) - this.fleetTypes.indexOf(b.serviceType) ||
              Number(a.fleetId !== null) - Number(b.fleetId !== null) ||
              (a.fleetName ?? '').localeCompare(b.fleetName ?? ''),
          );
        },
        error: (error) => {
          this.isLoading = false;
          if (this.handledAuthError(error)) return;
          this.loadError = isUnsupportedByApi(error) ? 'unsupported' : 'failed';
        },
      });
  }

  /** Optional: without it the page still works, it just cannot show or check the app fee. */
  private fetchServiceCharges(): void {
    this.apollo
      .query({ fetchPolicy: 'network-only', query: SERVICE_CHARGE_CONFIGS })
      .subscribe({
        next: (res: any) => {
          const rows: ServiceChargeConfig[] = res?.data?.['serviceChargeConfigs'] ?? [];
          this.serviceCharges = Object.fromEntries(rows.map((row) => [row.serviceType, row]));
        },
        error: () => {
          this.serviceCharges = {};
        },
      });
  }

  openAdd(modal: TemplateRef<any>): void {
    this.editing = null;
    this.form = this.emptyForm();
    this.loadFleetOptions();
    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  openEdit(row: FleetFeeDistribution, modal: TemplateRef<any>): void {
    this.editing = row;
    this.form = {
      serviceType: row.serviceType,
      scope: row.fleetId ? 'fleet' : 'default',
      fleetId: row.fleetId ?? '',
      operatorShareAmount: row.operatorShareAmount,
      driverIncentiveAmount: row.driverIncentiveAmount,
    };
    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  onServiceTypeChange(): void {
    this.form.fleetId = '';
    this.loadFleetOptions();
  }

  /** Fleets of the chosen type, for an override. The API returns at most 100 per page. */
  loadFleetOptions(): void {
    const fleetType = this.form.serviceType;
    this.isLoadingFleets = true;
    this.fleetOptions = [];

    this.apollo
      .query({
        fetchPolicy: 'network-only',
        query: ADMIN_FLEET_MANAGEMENT_LIST,
        variables: { input: { fleetType, page: 1, limit: 100 } },
      })
      .subscribe({
        next: (res: any) => {
          // A later type change wins over an earlier, slower response.
          if (this.form.serviceType !== fleetType) return;
          this.isLoadingFleets = false;
          const items = res?.data?.['adminFleetManagementList']?.items ?? [];
          this.fleetOptions = items
            .map((item: any) => ({
              fleetId: item.fleetId,
              operatorName: item.operatorName,
              fleetCode: item.fleetCode,
            }))
            .sort((a: any, b: any) => a.operatorName.localeCompare(b.operatorName));
        },
        error: (error) => {
          if (this.form.serviceType !== fleetType) return;
          this.isLoadingFleets = false;
          if (this.handledAuthError(error)) return;
          this.toastr.error('Could not load the Fleet Operators. Please try again.');
        },
      });
  }

  get formFlatFee(): number | null {
    return this.flatFeeFor(this.form.serviceType);
  }

  get formOkitoShare(): number | null {
    return this.okitoShare({
      serviceType: this.form.serviceType,
      operatorShareAmount: Number(this.form.operatorShareAmount) || 0,
      driverIncentiveAmount: Number(this.form.driverIncentiveAmount) || 0,
    });
  }

  private validationError(): string | null {
    const { scope, fleetId, operatorShareAmount, driverIncentiveAmount } = this.form;

    if (scope === 'fleet' && !fleetId) {
      return 'Select the Fleet Operator this applies to.';
    }
    for (const [value, label] of [
      [operatorShareAmount, 'Fleet Operator share'],
      [driverIncentiveAmount, 'Driver incentive'],
    ] as [number | null, string][]) {
      if (value === null || `${value}` === '' || Number.isNaN(Number(value))) {
        return `Enter the ${label.toLowerCase()}.`;
      }
      if (Number(value) < 0) {
        return `${label} must be 0 or more.`;
      }
    }

    const flatFee = this.formFlatFee;
    if (flatFee !== null && Number(operatorShareAmount) + Number(driverIncentiveAmount) > flatFee) {
      return `The Fleet Operator share and the driver incentive cannot exceed the ₱${flatFee} app fee.`;
    }

    if (!this.editing) {
      const targetFleetId = scope === 'fleet' ? fleetId : null;
      const exists = this.distributions.some(
        (row) => row.serviceType === this.form.serviceType && row.fleetId === targetFleetId,
      );
      if (exists) {
        return scope === 'fleet'
          ? 'This Fleet Operator already has a distribution. Edit it instead.'
          : `A ${this.fleetTypeLabel(this.form.serviceType)} default already exists. Edit it instead.`;
      }
    }

    return null;
  }

  save(successModal: TemplateRef<any>): void {
    const error = this.validationError();
    if (error) {
      this.toastr.error(error);
      return;
    }

    this.isSaving = true;
    const input: Record<string, any> = {
      serviceType: this.form.serviceType,
      operatorShareAmount: Number(this.form.operatorShareAmount),
      driverIncentiveAmount: Number(this.form.driverIncentiveAmount),
    };
    if (this.form.scope === 'fleet') {
      input['fleetId'] = this.form.fleetId;
    }
    // isActive is left out: Add starts enabled, Edit keeps the row's status.

    this.apollo
      .mutate({ mutation: UPSERT_FLEET_FEE_DISTRIBUTION, variables: { input } })
      .subscribe({
        next: ({ data }: any) => {
          this.isSaving = false;
          if (!data?.['upsertFleetFeeDistribution']) {
            this.toastr.error('The fee distribution was not saved. Please try again.');
            return;
          }
          this.modalService.dismissAll();
          this.successMessage = this.editing
            ? 'Fee distribution successfully updated'
            : 'Fee distribution successfully added';
          this.modalService.open(successModal, { backdrop: 'static', keyboard: false, size: 'lg' });
          this.fetchDistributions();
        },
        error: (error) => {
          this.isSaving = false;
          if (this.handledAuthError(error)) return;
          this.toastr.error(this.errorMessage(error, 'Error saving the fee distribution'));
        },
      });
  }

  confirmStatus(row: FleetFeeDistribution, modal: TemplateRef<any>): void {
    this.statusTarget = row;
    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  updateStatus(successModal: TemplateRef<any>): void {
    const row = this.statusTarget;
    if (!row) return;

    this.isSaving = true;
    const isActive = !row.isActive;
    this.apollo
      .mutate({
        mutation: UPDATE_FLEET_FEE_DISTRIBUTION_STATUS,
        variables: { input: { fleetFeeDistributionId: row.fleetFeeDistributionId, isActive } },
      })
      .subscribe({
        next: ({ data }: any) => {
          this.isSaving = false;
          const updated: FleetFeeDistribution | undefined = data?.['updateFleetFeeDistributionStatus'];
          if (!updated) {
            this.toastr.error('The status was not changed. Please try again.');
            return;
          }
          this.distributions = this.distributions.map((item) =>
            item.fleetFeeDistributionId === updated.fleetFeeDistributionId ? cloneDeep(updated) : item,
          );
          this.modalService.dismissAll();
          this.successMessage = isActive
            ? 'Fee distribution successfully enabled'
            : 'Fee distribution successfully disabled';
          this.modalService.open(successModal, { backdrop: 'static', keyboard: false, size: 'lg' });
        },
        error: (error) => {
          this.isSaving = false;
          this.modalService.dismissAll();
          if (this.handledAuthError(error)) return;
          this.toastr.error(this.errorMessage(error, 'Error changing the status'));
        },
      });
  }

  closeAllModals(): void {
    this.modalService.dismissAll();
  }

  /**
   * NestJS ValidationPipe rejections say only "Bad Request Exception"; the
   * real reasons are under `extensions.originalError.message`.
   */
  private errorMessage(error: any, fallback: string): string {
    for (const graphQLError of graphQLErrorsOf(error)) {
      const original = graphQLError?.extensions?.originalError?.message;
      const messages = (Array.isArray(original) ? original : [original]).filter(
        (message) => typeof message === 'string' && message.trim(),
      );
      if (messages.length) return messages.join(' ');
    }
    const message = graphQLErrorsOf(error)[0]?.message ?? error?.message;
    return message && message !== 'Bad Request Exception' ? message : fallback;
  }

  /** Logs out on an expired/forbidden session; returns true when it did. */
  private handledAuthError(error: any): boolean {
    // Refused by the admin's roles, not a dead session: say so and stay signed in.
    if (isMissingPermissionError(error)) {
      this.toastr.error(MISSING_PERMISSION_MESSAGE);
      return true;
    }

    const isAuthError =
      error?.message === 'Unauthorized' ||
      error?.networkError?.status === 403 ||
      error?.graphQLErrors?.[0]?.extensions?.status === 403 ||
      error?.errors?.[0]?.extensions?.status === 403;

    if (!isAuthError) return false;

    this.authStore.logout().subscribe(() => {
      this.router.navigate(['/admin/login']);
    });
    return true;
  }
}
